import { Link } from 'react-router-dom'
import type { CaseDetail, CaseSource, CaseUpdate, Money } from '../../api/client'
import { standTon, tagKurz, wirkungTon } from '../../hooks/faelleRegeln'
import { label } from '../../pages/researchVocabulary'
import { Loading } from '../common/Feedback'
import s from './FallDetail.module.scss'

const datumLang = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
const zahl = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })

/** „≈ 24,44 Mrd EUR" — eine Schätzung, auf den Cent genau wäre falsch. */
export function betragKurz(geld: Money | null): string {
  if (!geld) return '—'
  const n = Number(geld.amount)
  if (Math.abs(n) >= 1e9) return `≈ ${zahl.format(n / 1e9)} Mrd ${geld.currency}`
  if (Math.abs(n) >= 1e6) return `≈ ${zahl.format(n / 1e6)} Mio ${geld.currency}`
  return `${zahl.format(n)} ${geld.currency}`
}

function tagLang(iso: string | null): string {
  return iso ? datumLang.format(new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso)) : '—'
}

/** Der jüngste Eintrag, der die Wahrscheinlichkeit verschoben hat. */
function verschiebung(updates: CaseUpdate[]): { von: number; nach: number } | null {
  const u = [...updates]
    .sort((a, b) => (b.event_at ?? b.recorded_at).localeCompare(a.event_at ?? a.recorded_at))
    .find((x) => x.probability_before !== null && x.probability_after !== null
                 && Number(x.probability_before) !== Number(x.probability_after))
  return u ? { von: Number(u.probability_before), nach: Number(u.probability_after) } : null
}

function richtungTon(direction: string | null): string {
  return direction === 'POSITIVE' ? 'good' : direction === 'NEGATIVE' ? 'bad'
    : direction === 'UNCLEAR' ? 'warn' : 'muted'
}

/**
 * Das Falldetail — der rechte Teil der Master-Detail-Ansicht.
 *
 * Oben das Blättern, dann der Fall: Art, Stand, Betrifft, Beginn und
 * Erfassung, der Vorgang in der Serife, das Aktenzeichen. Darunter fünf
 * Kennzahlen (Wirkung, Wahrscheinlichkeit, Last, Belastung, nächste
 * Prüfung), Sachstand mit Einschätzung, der Verlauf und die Belege.
 */
export function FallDetail({ fall, laedt, position, onVorherige, onNaechste }: {
  fall: CaseDetail | null
  laedt?: boolean
  /** „Heute · 2 von 5". */
  position: string
  onVorherige: () => void
  onNaechste: () => void
}) {
  return (
    <div className={s.detail}>
      <div className={s.blaettern}>
        <span>{position}</span>
        <span className={s.knoepfe}>
          <button type="button" onClick={onVorherige}>‹ vorheriger</button>
          <button type="button" onClick={onNaechste}>nächster ›</button>
        </span>
      </div>
      {fall ? <Artikel fall={fall} /> : laedt ? <Loading>lädt den Fall …</Loading> : null}
    </div>
  )
}

function Artikel({ fall }: { fall: CaseDetail }) {
  const haupt = fall.instruments.find((i) => i.role === 'PRIMARY') ?? fall.instruments[0]
  const schub = verschiebung(fall.updates)
  const trend = schub ? `${schub.von.toFixed(0)} → ${schub.nach.toFixed(0)} %`
    : fall.probability !== null ? `${Number(fall.probability).toFixed(0)} %` : '—'
  const trendTon = schub ? (schub.nach > schub.von ? 'bad' : 'good') : 'neutral'
  const pruefTage = fall.next_review_at
    ? Math.round((new Date(`${fall.next_review_at}T12:00:00`).getTime() - new Date().setHours(12, 0, 0, 0)) / 86_400_000)
    : null
  const pruefungIn = pruefTage === null ? 'nicht gesetzt' : pruefTage < 0 ? 'überfällig'
    : pruefTage === 0 ? 'heute' : pruefTage === 1 ? 'morgen' : `in ${pruefTage} Tagen`
  const updates = [...fall.updates]
    .sort((a, b) => (b.event_at ?? b.recorded_at).localeCompare(a.event_at ?? a.recorded_at))
  const zustaendig = [fall.authority, fall.jurisdiction].filter(Boolean).join(' · ') || '—'

  return (
    <article className={s.artikel}>
      <div className={s.kopf} data-ton={wirkungTon(fall.regime_impact)}>
        <div className={s.inhalt}>
          <div className={s.zeile}>
            <span className={s.art}>{label('case_type', fall.case_type)}</span>
            <span className={s.stand} data-ton={standTon(fall.status)}>{label('case_status', fall.status)}</span>
            <span className={s.betrifft}>
              Betrifft{' '}
              {haupt ? <Link to={`/instruments/${haupt.id}`}>{haupt.name}</Link> : <span>—</span>}
              {haupt?.role === 'PRIMARY' && ' hauptbetroffen'}
              {fall.instruments.length > 1 && ` +${fall.instruments.length - 1}`}
              {' · zuständig '}{zustaendig}
            </span>
            <span className={s.daten}>Beginn {tagKurz(fall.opened_at)} · erstellt {tagKurz(fall.created)}</span>
          </div>
          <h2 className={s.titel}>{fall.title}</h2>
          {fall.reference && <div className={s.referenz}>{fall.reference}</div>}
        </div>
      </div>

      <div className={s.kennzahlen}>
        <div className={s.kennzahl}>
          <div className={s.kLabel}>Wirkung</div>
          <div className={s.kWert} data-ton={wirkungTon(fall.regime_impact)}>
            {fall.regime_impact}<span className={s.kEinheit}> /4</span>
          </div>
          <div className={s.kSub}>{label('trading_impact', fall.trading_impact)}</div>
        </div>
        <div className={s.kennzahl}>
          <div className={s.kLabel}>Wahrsch.</div>
          <div className={s.kWert} data-ton={trendTon}>{trend}</div>
          <div className={s.kSub}>{fall.confidence !== null ? `Sicherheit ${fall.confidence} %` : 'ohne Sicherheit'}</div>
        </div>
        <div className={s.kennzahl}>
          <div className={s.kLabel}>Last</div>
          <div className={s.kWert}>{fall.overhang_score ?? '—'}</div>
          <div className={s.kSub}>{fall.updates.length} {fall.updates.length === 1 ? 'Update' : 'Updates'}</div>
        </div>
        <div className={s.kennzahl}>
          <div className={s.kLabel}>Belastung</div>
          <div className={s.kWertKlein}>{fall.exposure_base ? betragKurz(fall.exposure_base) : 'nicht geschätzt'}</div>
          <div className={s.kSub}>
            {fall.exposure_base
              ? `${label('exposure_basis', fall.exposure_basis)} · ${betragKurz(fall.exposure_low)} / ${betragKurz(fall.exposure_high)}`
              : 'Noch nicht abgeschätzt'}
          </div>
        </div>
        <div className={s.kennzahl}>
          <div className={s.kLabel}>Nächste Prüfung</div>
          <div className={s.kWertMono}>{tagLang(fall.next_review_at)}</div>
          <div className={s.kSub} data-dringend={pruefTage !== null && pruefTage <= 2 || undefined}>{pruefungIn}</div>
        </div>
      </div>

      <div className={s.abschnitt}>
        <div className={s.abschnittTitel}>Sachstand</div>
        <p className={s.sachstand}>{fall.summary ?? 'Sachstand noch nicht erfasst.'}</p>
        <div className={s.einschaetzung}>
          <b>Einschätzung</b> {fall.analyst_assessment ?? 'Noch keine Einschätzung hinterlegt.'}
        </div>
      </div>

      <div className={s.abschnitt}>
        <div className={s.abschnittKopf}>
          <div className={s.abschnittTitel}>Verlauf</div>
          <span className={s.anzahl}>{updates.length}</span>
        </div>
        {updates.length === 0 && <div className={s.keine}>Noch keine Aktualisierung.</div>}
        {updates.map((u) => (
          <div key={u.id} className={s.update}>
            <div className={s.updateDatum}>{tagLang(u.event_at ?? u.recorded_at)}</div>
            <div className={s.updateBalken} data-ton={richtungTon(u.direction)} />
            <div>
              <div className={s.updateTitel}>{u.headline}</div>
              <div className={s.updateMeta}>
                {label('update_type', u.update_type)}
                {u.materiality !== null && ` · Bedeutung ${u.materiality}/4`}
                {u.probability_before !== null && u.probability_after !== null
                  && Number(u.probability_before) !== Number(u.probability_after)
                  && ` · Wahrscheinlichkeit ${Number(u.probability_before).toFixed(0)} → ${Number(u.probability_after).toFixed(0)} %`}
                {u.event_at && u.recorded_at.slice(0, 10) !== u.event_at && ` · erfasst ${tagKurz(u.recorded_at)}`}
              </div>
              {u.fact_summary && <div className={s.updateFakt}>{u.fact_summary}</div>}
              {u.analyst_comment && <div className={s.updateAnalyse}>{u.analyst_comment}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className={s.abschnitt} data-letzter>
        <div className={s.abschnittKopf}>
          <div className={s.abschnittTitel}>Belege</div>
          <span className={s.anzahl}>{fall.sources.length}</span>
        </div>
        {fall.sources.length === 0 && <div className={s.keine}>Keine Belege hinterlegt.</div>}
        {fall.sources.map((q) => <Beleg key={q.id} quelle={q} />)}
      </div>
    </article>
  )
}

function Beleg({ quelle }: { quelle: CaseSource }) {
  const marke = quelle.publisher ?? label('source_type', quelle.source_type)
  return (
    <div className={s.beleg}>
      <span className={s.belegMarke} data-amtlich={quelle.official || undefined}>{marke}</span>
      <div>
        {quelle.url
          ? <a href={quelle.url} target="_blank" rel="noreferrer">{quelle.title ?? quelle.url}</a>
          : <span>{quelle.title ?? '—'}</span>}
        {quelle.published_at && <span className={s.belegDatum}> · {tagLang(quelle.published_at)}</span>}
        {quelle.excerpt && <div className={s.belegZitat}>„{quelle.excerpt}“</div>}
      </div>
    </div>
  )
}
